//React
import React, { useId, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Chart } from "react-chartjs-2";
import { Tooltip } from "react-tooltip";
import { cssToken } from "../../utilities/cssToken";
import {
	registerCharts,
	chartTheme,
	baseOptions,
	legendPlugin,
	formatValue,
	tooltipText,
	unitFor,
	unitOf,
} from "./chartTheme";
import { buildTrendSeries } from "./trendData";
//CSS
import "./charts.scss";

/** The token a small-base point (A.6 `minBase`) is painted with: the muted role's lightest shade. */
export const SMALL_BASE_TOKEN = "--as-chart-role-muted-3";

/**
 * The hover line for one point: "Security block: 92.41% of flagged (25,225
 * records, 25.48% of checked)", the tooltip columns (A.6) formatted by type
 * and named by their label in lower case, plus the small-base note when the
 * point has one. A % column labelled "% of …" reads as "12.5% of …", not
 * "12.5% % of …"; a count of 1 takes the singular ("1 record").
 */
export function tooltipLine(seriesLabel, yColumn, point, tooltipColumns) {
	const valueWithUnit = (col, value) => {
		const text = formatValue(col.type, value, "cell");
		const label = String(col.label ?? col.key);
		if (col.type === "pct" && label.startsWith("% "))
			return value == null ? text : `${text}${label.slice(1)}`;
		return `${text} ${col.type === "int" ? unitFor(label, value) : unitOf(label)}`;
	};
	const y = formatValue(yColumn.type, point?.y, "cell");
	const ofBase =
		yColumn.type === "pct" && String(yColumn.label ?? "").startsWith("% ") && point?.y != null
			? yColumn.label.slice(1)
			: "";
	const extras = (tooltipColumns ?? [])
		.filter((col) => point?.extra?.[col.key] !== undefined)
		.map((col) => valueWithUnit(col, point.extra[col.key]));
	const parts = [`${seriesLabel}: ${y}${ofBase}`];
	if (extras.length) parts.push(`(${extras.join(", ")})`);
	if (point?.small) parts.push(`· ${point.small.note}`);
	return parts.join(" ");
}

/**
 * Rescales a whole stack to the series shown (ASB-4377): each bar always fills
 * the axis, split among the visible datasets, so hiding the biggest series
 * makes the rest readable instead of leaving slivers. Runs before every
 * update, so a legend toggle (setDatasetVisibility + update) rescales too.
 * Each dataset keeps its true shares in `shares`; the tooltip reads the
 * series' points, never the rescaled heights.
 */
export const rescaleToShownPlugin = {
	id: "rescaleToShown",
	beforeUpdate(chart) {
		const sets = chart.data.datasets;
		if (!sets.length || !sets.every((d) => Array.isArray(d.shares))) return;
		const length = sets[0].shares.length;
		const totals = Array.from({ length }, (_, j) =>
			sets.reduce((sum, d, i) => (chart.isDatasetVisible(i) ? sum + (d.shares[j] ?? 0) : sum), 0)
		);
		sets.forEach((d) => {
			d.data = d.shares.map((v, j) => (v == null || totals[j] <= 0 ? null : v / totals[j]));
		});
	},
};

/**
 * True when every x's stack, over all series (hidden ones too), adds up to
 * 100% or has no data: the y values are shares of one whole.
 */
export function isWholeStack(series, length) {
	if (!series.length || !length) return false;
	let any = false;
	for (let i = 0; i < length; i++) {
		const values = series.map((s) => s.data[i]).filter((v) => v !== null && v !== undefined);
		if (!values.length) continue;
		const sum = values.reduce((a, v) => a + v, 0);
		if (sum === 0) continue;
		if (Math.abs(sum - 1) > 1e-9) return false;
		any = true;
	}
	return any;
}

/**
 * A time series (ASB-4377): `stacked_bar` or `line` over `x` (days), one
 * series per distinct value of the dataset's `series` column, `y` summed per
 * (x, series) — or num ÷ den when `y` is a ratio. Takes a manifest dataset and
 * rows, never Chart.js options: the theme decides fonts, grid, axes and
 * tooltip, and the palette colours each series by its enum role (by value, so
 * "paywall" is amber whatever the range), unmapped values taking the
 * categorical tokens in a fixed order. A `muted` series starts hidden
 * (Chart.js `hidden`), so the axis scales to the rest; the HTML legend shows
 * it struck through and a click toggles it on. A series value's `help` shows
 * when its legend entry is hovered or focused (one tooltip for the legend);
 * the hover shows the `tooltipColumns` beside
 * the y value; a point under the y column's `minBase` is painted muted. The
 * hovered values are mirrored into a visually hidden live region, since the
 * canvas tooltip is paint only.
 */
function TrendChart({ dataset, rows, ariaLabel, from, to, dataFrom }) {
	registerCharts();
	const uid = useId().replace(/:/g, "");
	const legendId = `as-chart-legend-${uid}`;
	const helpTipId = `as-chart-legend-help-${uid}`;
	const theme = useMemo(chartTheme, []);
	const readout = useRef(null);
	// With a range the x axis spans every day of it (buildTrendSeries), so a
	// day the pipeline did not produce is a gap, not a day that closes up.
	const range = useMemo(
		() => (from && to ? { from, to, dataFrom: dataFrom ?? null } : null),
		[from, to, dataFrom]
	);
	const { xs, series, yColumn, tooltipColumns } = useMemo(
		() => buildTrendSeries(dataset, rows ?? [], cssToken, range),
		[dataset, rows, range]
	);
	const stacked = dataset.chart === "stacked_bar";
	const xColumn = (dataset.columns ?? []).find((c) => c.key === dataset.x);
	const xType = dataset.x === "day" ? "date" : xColumn?.type ?? "text";
	const smallColor = cssToken(SMALL_BASE_TOKEN);
	const pointColors = (s) =>
		s.points.some((p) => p.small) ? s.points.map((p) => (p.small ? smallColor : s.color)) : s.color;
	const byLabel = new Map(series.map((s) => [s.label ?? s.value, s]));
	const seriesColumn = (dataset.columns ?? []).find((c) => c.key === dataset.series);
	const whole = stacked && yColumn.type === "pct" && isWholeStack(series, xs.length);

	const datasets = series.map((s) =>
		stacked
			? {
					type: "bar",
					label: s.label ?? s.value,
					data: s.data,
					...(whole ? { shares: s.data } : {}),
					backgroundColor: pointColors(s),
					borderColor: s.color,
					stack: "total",
					hidden: s.hidden,
					maxBarThickness: 28,
					borderRadius: 2,
					borderSkipped: false,
			  }
			: {
					type: "line",
					label: s.label ?? s.value,
					data: s.data,
					borderColor: s.color,
					backgroundColor: s.color,
					pointBackgroundColor: pointColors(s),
					borderWidth: 2,
					pointRadius: 2,
					pointHoverRadius: 4,
					tension: 0.25,
					fill: false,
					hidden: s.hidden,
			  }
	);
	// The HTML legend lists datasets last-first (it was written for the Console
	// chart, where the line is drawn after the bars). A stack reads top-down
	// that way; a set of lines reads backwards, so lines go in reversed.
	const data = {
		labels: xs.map((x) => formatValue(xType, x, "axis")),
		datasets: stacked ? datasets : datasets.reverse(),
	};
	const options = baseOptions(theme, {
		legendId,
		stacked,
		yTitle: yColumn.label,
		yTick: (value) => formatValue(yColumn.type, value, "axis"),
		tooltipTitle: (items) =>
			items.length ? formatValue(xType, xs[items[0].dataIndex], "cell") : "",
		tooltipLabel: (ctx) => {
			const s = byLabel.get(ctx.dataset.label);
			const point = s?.points?.[ctx.dataIndex] ?? { y: ctx.parsed.y, extra: {} };
			return tooltipLine(ctx.dataset.label, yColumn, point, tooltipColumns);
		},
		tooltipExternal: (model) => {
			const el = readout.current;
			if (!el) return;
			const text = tooltipText(model);
			if (el.textContent !== text) el.textContent = text;
		},
	});
	// A stack of shares that each add up to 100% (Flagged records by reason)
	// always fills the axis, split among the series shown (rescaleToShownPlugin).
	// The height is then a share of what is shown, which changes with the
	// legend, so the axis keeps its grid but no % ticks; the hover gives the
	// true values.
	if (whole) {
		options.scales.y.max = 1;
		options.scales.y.ticks.callback = () => "";
		options.scales.y.title.text = `Share of ${unitOf(seriesColumn?.label ?? "series")}s shown`;
	}
	options.plugins.htmlLegend.itemHelp = (text) => byLabel.get(text)?.help ?? null;
	options.plugins.htmlLegend.helpTooltipId = helpTipId;
	// A point with nothing to say (a day with no rows) stays out of the hover:
	// the title still names the day, with no line reading "—" per series.
	options.plugins.tooltip.filter = (item) =>
		item.parsed?.y !== null && item.parsed?.y !== undefined;

	return (
		<div className="as-chart as-chart--trend">
			<div className="as-chart__body">
				<Chart
					type={stacked ? "bar" : "line"}
					data={data}
					options={options}
					plugins={whole ? [legendPlugin, rescaleToShownPlugin] : [legendPlugin]}
					role="img"
					aria-label={ariaLabel ?? `${dataset.title ?? yColumn.label} by ${dataset.x}`}
				/>
			</div>
			<p
				ref={readout}
				className="as-chart__readout"
				aria-live="polite"
			/>
			<div
				id={legendId}
				className="as-chart-legend"
			/>
			<Tooltip
				id={helpTipId}
				place="top"
				className="as-hovertip__tip"
			/>
		</div>
	);
}

TrendChart.propTypes = {
	/** a manifest dataset (design A.2 / A.6) with chart "stacked_bar" | "line", x, series, y */
	dataset: PropTypes.shape({
		id: PropTypes.string,
		title: PropTypes.string,
		chart: PropTypes.oneOf(["stacked_bar", "line"]).isRequired,
		x: PropTypes.string.isRequired,
		series: PropTypes.string,
		y: PropTypes.string.isRequired,
		columns: PropTypes.array,
		tooltipColumns: PropTypes.arrayOf(PropTypes.string),
	}).isRequired,
	/** the API's rows for the range: `{ day, ...stored data }` */
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	ariaLabel: PropTypes.string,
	/** the range the rows were read for ("YYYY-MM-DD"); with both, the x axis spans every day of it */
	from: PropTypes.string,
	to: PropTypes.string,
	/** the panel's first complete day (A.6 `dataFrom`): the axis starts there when `from` is earlier */
	dataFrom: PropTypes.string,
};

export default TrendChart;
